"use client";

// La Marketplace : des appels d'offres publiés, saisis à la main ou sourcés (le BOAMP et TED, app/sourcing), qu'on
// prend pour en faire un projet. On les cherche par lieu d'abord : une région ou un département, ou une ville et un
// rayon autour, sur l'adresse du chantier quand on l'a trouvée ; puis par ce que l'avis dit du chantier et ce que Jev
// en lit (corps d'état, nature, ouvrage, montant, lots, complexité). L'entreprise enregistre ses filtres : sur cette
// requête, Jev mesure ce que chaque annonce vaut pour elle (la pile du match). En liste ou sur la carte. Prendre
// importe le DCE (retiré de sa plateforme quand elle le permet), puis l'orchestrateur confie son analyse à un agent du
// projet : on bascule sur l'onglet Projets.

import dynamic from "next/dynamic";
import Link from "next/link";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
} from "react";
import {
  api,
  ErreurApi,
  LIBELLES_PRECISION,
  type Annonce,
  type Libelles,
  type Montant,
  type Precision,
  type Recherche,
  type RequeteEntreprise,
  type Seuils,
} from "@/lib/api";
import { dateLongue, echeance, joursAvant } from "@/lib/dates";
import { ecouter } from "@/lib/flux";
import { NIVEAUX, Pile, teinte } from "./Pile";
import { Trait } from "./Squelette";
import "./marketplace.css";

const CarteAnnonces = dynamic(
  () => import("./CarteAnnonces").then((m) => m.CarteAnnonces),
  { ssr: false, loading: () => <div className="carte-annonces" /> },
);

const PROCEDURES: Record<string, string> = {
  "appel-offres-ouvert": "Appel d'offres ouvert",
  "appel-offres-restreint": "Appel d'offres restreint",
  "procedure-adaptee": "Procédure adaptée",
  "procedure-negociee": "Procédure négociée",
  "dialogue-competitif": "Dialogue compétitif",
  autre: "Autre procédure",
};

const ORIGINES: Record<string, string> = {
  manuel: "Saisie à la main",
  boamp: "BOAMP",
  ted: "TED (Europe)",
};

const EDITEURS: Record<string, string> = {
  atexo: "Atexo",
  aws: "AWS-Achat",
  achatpublic: "Achatpublic",
  "marches-securises": "Marchés Sécurisés",
  dematis: "Dematis",
  omnikles: "Omnikles",
  xmarches: "Xmarchés",
  klekoon: "Klekoon",
  "centrale-des-marches": "Centrale des Marchés",
  synapse: "Synapse",
  francemarches: "France Marchés",
  "eu-supply": "EU-Supply",
};

const CATEGORIES: Record<string, string> = {
  Travaux: "Travaux",
  Services: "Services",
  Fournitures: "Fournitures",
};
// La précision minimale de l'adresse du chantier.
const PRECISIONS_LIEU: Record<string, string> = {
  adresse: "À l'adresse",
  rue: "À la rue",
  ville: "À la ville",
  departement: "Au département",
};
const ACCORDS: Record<string, string> = {
  true: "Accords-cadres",
  false: "Hors accords-cadres",
};
const RAYONS = [5, 10, 25, 50, 100];
const LOTS: Record<string, string> = {
  "1": "Lot unique",
  "2-5": "2 à 5 lots",
  "6-": "6 lots et plus",
};
const MATCHS = [30, 50, 70, 90];
const PAGE = 50;
const CLE_FILTRES = "minibreek.marketplace.filtres";
const SANS_LIBELLES: Libelles = {
  corps: {},
  natures: {},
  ouvrages: {},
  traits: {},
  complexites: [],
  tranches: {},
};

type Filtres = {
  q: string;
  zone: string; // "" (partout), "r:11" (une région), "d:91" (un département)
  ville: string;
  rayon: number;
  // Les choix multiples : vide, tout passe ; sinon, l'un d'eux.
  categorie: string[];
  origine: string[];
  precision: string;
  retirable: boolean;
  // Le chantier : ce que l'avis en dit et ce que Jev en lit.
  corps: string[];
  nature: string[];
  ouvrage: string[];
  traits: string[]; // tous à la fois
  complexite: string[]; // des niveaux, "0" à "3"
  montant: string[]; // des tranches (`libelles.tranches`)
  lots: string[]; // des clés de LOTS
  accord_cadre: string; // "", "true", "false"
  match_min: number;
  tri: string;
  vue: "liste" | "carte";
};
const FILTRES: Filtres = {
  q: "",
  zone: "",
  ville: "",
  rayon: 25,
  categorie: [],
  origine: [],
  precision: "",
  retirable: false,
  corps: [],
  nature: [],
  ouvrage: [],
  traits: [],
  complexite: [],
  montant: [],
  lots: [],
  accord_cadre: "",
  match_min: 0,
  tri: "date_limite",
  vue: "liste",
};
const MULTIPLES = [
  "categorie",
  "origine",
  "corps",
  "nature",
  "ouvrage",
  "traits",
  "complexite",
  "montant",
  "lots",
] as const;

const taille = (octets: number) =>
  octets >= 1e6
    ? `${Math.round(octets / 1e6)} Mo`
    : `${Math.max(1, Math.round(octets / 1e3))} ko`;

const euros = (n: number) =>
  n >= 1e6
    ? `${(n / 1e6).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} M€`
    : n >= 1e3
      ? `${Math.round(n / 1e3)} k€`
      : `${Math.round(n)} €`;

function texteMontant(m: Montant): string {
  const f =
    m.haut == null
      ? `plus de ${euros(m.bas)}`
      : m.bas === m.haut
        ? euros(m.bas)
        : m.bas
          ? `${euros(m.bas)} à ${euros(m.haut)}`
          : `moins de ${euros(m.haut)}`;
  return m.estime ? `≈ ${f} HT (estimé)` : `${f} HT`;
}

// Une tranche de montant en peu de mots, pour sa puce : « 100 k€ à 500 k€ ».
function trancheCourte(t: { bas: number; haut: number | null }): string {
  return t.haut == null
    ? `Plus de ${euros(t.bas)}`
    : t.bas
      ? `${euros(t.bas)} à ${euros(t.haut)}`
      : `Moins de ${euros(t.haut)}`;
}

const duree = (mois: number) =>
  mois >= 24 && mois % 12 === 0 ? `${mois / 12} ans` : `${mois} mois`;

// Les paramètres de la recherche (et de la requête enregistrée) : la route `recherche` d'app/sourcing/routes.py.
function parametres(
  f: Filtres,
  libelles: Libelles,
): Record<string, string | number | boolean> {
  const [genre, code] = f.zone.split(":");
  return {
    q: f.q,
    dep: genre === "d" ? code : "",
    region: genre === "r" ? code : "",
    ville: f.ville,
    rayon: f.ville ? f.rayon : 0,
    categorie: f.categorie.join(","),
    origine: f.origine.join(","),
    precision: f.precision,
    retirable: f.retirable,
    corps: f.corps.join(","),
    nature: f.nature.join(","),
    ouvrage: f.ouvrage.join(","),
    traits: f.traits.join(","),
    complexite: f.complexite.join(","),
    tranche: f.montant.filter((t) => t in libelles.tranches).join(","),
    lots: f.lots.join(","),
    accord_cadre: f.accord_cadre,
    match_min: f.match_min,
  };
}

// L'inverse : les filtres d'une requête enregistrée.
function depuisCriteres(
  c: Record<string, string | number | boolean>,
  libelles: Libelles,
  actuels: Filtres,
): Filtres {
  const s = (k: string) => (c[k] == null ? "" : String(c[k]));
  const l = (k: string) => (s(k) ? s(k).split(",") : []);
  // Une requête d'avant les choix multiples : un intervalle de montant, de lots, une complexité maximale.
  const tranche =
    Object.entries(libelles.tranches).find(
      ([, t]) =>
        t.bas === Number(c.montant_min ?? 0) &&
        (t.haut ?? 0) === Number(c.montant_max ?? 0),
    )?.[0] ?? "";
  const lots =
    s("lots_max") === "1"
      ? "1"
      : s("lots_min") || s("lots_max")
        ? `${s("lots_min")}-${s("lots_max")}`
        : "";
  const complexiteMax =
    s("complexite_max") === "" ? 3 : Number(c.complexite_max);
  return {
    ...FILTRES,
    q: s("q"),
    zone: s("region")
      ? `r:${s("region").split(",")[0]}`
      : s("dep")
        ? `d:${s("dep").split(",")[0]}`
        : "",
    ville: s("ville"),
    rayon: Number(c.rayon) || 25,
    categorie: l("categorie"),
    origine: l("origine"),
    precision: s("precision"),
    retirable: c.retirable === true || s("retirable") === "true",
    corps: l("corps"),
    nature: l("nature"),
    ouvrage: l("ouvrage"),
    traits: l("traits"),
    complexite: s("complexite")
      ? l("complexite")
      : complexiteMax < 3
        ? ["0", "1", "2", "3"].slice(0, complexiteMax + 1)
        : [],
    montant: s("tranche") ? l("tranche") : tranche ? [tranche] : [],
    lots: s("lots") ? l("lots") : lots in LOTS ? [lots] : [],
    accord_cadre: s("accord_cadre"),
    match_min: Number(c.match_min) || 0,
    tri: actuels.tri,
    vue: actuels.vue,
  };
}

function lireFiltres(): Filtres {
  try {
    const f = JSON.parse(window.sessionStorage.getItem(CLE_FILTRES) ?? "null");
    if (!f) return FILTRES;
    // Des filtres gardés avant les choix multiples : une valeur seule devient une liste.
    for (const k of MULTIPLES)
      if (!Array.isArray(f[k])) f[k] = f[k] ? [String(f[k])] : [];
    return { ...FILTRES, ...f };
  } catch {
    return FILTRES;
  }
}

// Le lieu du chantier comme on le connaît : l'adresse trouvée si elle est assez précise, sinon le libellé de l'avis.
function Lieu({ a }: { a: Annonce }) {
  const adr = a.adresse;
  const precision: Precision = adr?.precision ?? "inconnue";
  const texte =
    adr && (precision === "adresse" || precision === "rue")
      ? adr.texte
      : a.lieu;
  return (
    <span className="annonce-lieu">
      {texte || "Lieu inconnu"}
      <span
        className={`precision precision-${precision}`}
        title={
          adr?.point === "acheteur"
            ? "L'adresse de l'acheteur : l'avis ne dit pas où est le chantier"
            : `Chantier situé ${LIBELLES_PRECISION[precision]}`
        }
      >
        {adr?.point === "acheteur" ? "acheteur" : LIBELLES_PRECISION[precision]}
      </span>
      {a.distance_km != null && (
        <span className="discret"> · à {a.distance_km} km</span>
      )}
    </span>
  );
}

// Un volet de l'en-tête (les filtres, le Fit) : un bouton et son popover, posé sous lui. Le navigateur le met
// au-dessus de tout, et le ferme d'un clic ailleurs ou par Échap.
function Volet({
  id,
  classe,
  titre,
  droite,
  bouton,
  children,
}: {
  id: string;
  classe: string;
  titre?: string;
  droite?: boolean;
  bouton: ReactNode;
  children: ReactNode;
}) {
  // Le clic passe avant l'ouverture : on place le volet sous le bouton, d'un bord à l'autre sur un écran étroit.
  // Une fois ouvert (avant qu'il soit peint), on le mesure et on le ramène dans la fenêtre s'il en dépasse.
  const placer = (e: MouseEvent<HTMLButtonElement>) => {
    const v = document.getElementById(id);
    if (!v) return;
    const b = e.currentTarget.getBoundingClientRect();
    const l = window.innerWidth;
    if (l < 600) {
      v.style.top = `${b.bottom + 6}px`;
      v.style.left = v.style.right = "8px";
      return;
    }
    v.style.top = `${b.bottom + 6}px`;
    v.style.right = "auto";
    v.style.left = "0px";
    requestAnimationFrame(() => {
      const largeur = v.offsetWidth;
      const voulu = droite ? b.right - largeur : b.left;
      v.style.left = `${Math.max(8, Math.min(voulu, l - largeur - 8))}px`;
    });
  };
  return (
    <>
      <button
        type="button"
        className={classe}
        popoverTarget={id}
        onClick={placer}
        title={titre}
      >
        {bouton}
      </button>
      <div id={id} popover="auto" className="volet">
        {children}
      </div>
    </>
  );
}

// Une ligne du volet des filtres : son nom, puis des puces à cocher, plusieurs à la fois (sauf `seul`), avec le
// nombre d'annonces de chacune. Les nombres sont ceux de la sélection : une fois un choix fait dans la ligne, ceux des
// autres puces ne disent plus ce qu'elles ajouteraient, on les tait.
function Puces({
  legende,
  options,
  comptes,
  choisis,
  seul,
  onChange,
}: {
  legende: string;
  options: Record<string, string>;
  comptes?: Record<string, number>;
  choisis: string[];
  seul?: boolean;
  onChange: (choisis: string[]) => void;
}) {
  const libre = choisis.length === 0;
  return (
    <fieldset className="puces">
      <legend>{legende}</legend>
      <div>
        {Object.entries(options).map(([k, l]) => {
          const pris = choisis.includes(k);
          const n = comptes ? (comptes[k] ?? 0) : null;
          return (
            <button
              key={k}
              type="button"
              className={`puce${pris ? " actif" : ""}${libre && n === 0 ? " nulle" : ""}`}
              aria-pressed={pris}
              onClick={() =>
                onChange(
                  pris
                    ? choisis.filter((x) => x !== k)
                    : seul
                      ? [k]
                      : [...choisis, k],
                )
              }
            >
              {l}
              {n !== null && (libre || pris) && <span>{n}</span>}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

const dollars = (n: number) =>
  `${n.toLocaleString("fr-FR", { maximumFractionDigits: 4 })} $`;

// Les critères, comparables : ce qui ne filtre pas (vide, zéro, faux) et le match minimal (un affichage) ne comptent pas.
function empreinte(c: Record<string, string | number | boolean>): string {
  return JSON.stringify(
    Object.entries(c)
      .filter(([k, v]) => k !== "match_min" && v !== "" && v !== 0 && !!v)
      .map(([k, v]) => [k, String(v)])
      .sort(),
  );
}

// Le Fit (le mot de mutuo) : ce que la recherche vaut pour l'entreprise, d'un coup d'œil. Un chiffre étroit (les
// annonces au-dessus du seuil bas), le nom, et la gamme des scores en cinq verts. Le volet règle les seuils,
// enregistre la requête et relance le match.
function Fit({
  requete,
  scores,
  perime,
  occupe,
  matchMin,
  onMatchMin,
  onEnregistrer,
  onAppliquer,
  onRelancer,
}: {
  requete: RequeteEntreprise;
  scores: number[];
  perime: boolean;
  occupe: boolean;
  matchMin: number;
  onMatchMin: (m: number) => void;
  onEnregistrer: (seuils: Seuils) => void;
  onAppliquer: () => void;
  onRelancer: () => void;
}) {
  const [seuils, setSeuils] = useState(requete.seuils);
  useEffect(() => setSeuils(requete.seuils), [requete.seuils]);
  const comptes: Record<string, number> = {};
  for (const s of scores) {
    const t = teinte(s, requete.seuils);
    comptes[t] = (comptes[t] ?? 0) + 1;
  }
  const utiles = scores.length - (comptes.n1 ?? 0);
  const run = requete.dernier_run;
  const cours = requete.en_cours;
  const etat = cours
    ? "en-cours"
    : !requete.enregistree
      ? "vide"
      : perime
        ? "perime"
        : "";
  const nom = cours
    ? `sur ${cours.total}`
    : !requete.enregistree
      ? "fit à régler"
      : perime
        ? "filtres changés"
        : `fit ${requete.nom}`;
  // La rampe du volet : le vert pâle jusqu'au seuil bas, trois tiers jusqu'au seuil haut, le vert foncé au-delà.
  const pas = (seuils.haut - seuils.bas) / 3;
  const bornes = [
    0,
    seuils.bas,
    seuils.bas + pas,
    seuils.haut - pas,
    seuils.haut,
    100,
  ];
  const largeurs = NIVEAUX.map((_, i) => bornes[i + 1] - bornes[i]);
  const seuilsChanges =
    seuils.bas !== requete.seuils.bas || seuils.haut !== requete.seuils.haut;

  return (
    <Volet
      id="volet-fit"
      classe={`fit${etat ? ` fit-${etat}` : ""}`}
      droite
      titre={
        cours
          ? `Match en cours : ${cours.faits} annonces sur ${cours.total}`
          : requete.enregistree
            ? `${utiles} annonces sur ${scores.length} passent le seuil de ${requete.seuils.bas} pour ${requete.nom}`
            : `Enregistrez des filtres pour ${requete.nom}`
      }
      bouton={
        <>
          <span className="fit-chiffre">
            {cours ? cours.faits : requete.enregistree ? utiles : "–"}
          </span>
          <span className="fit-nom">{nom}</span>
          <span className="fit-gamme" aria-hidden>
            {cours ? (
              <span
                className="fit-progres"
                style={{
                  width: `${(cours.faits / (cours.total || 1)) * 100}%`,
                }}
              />
            ) : (
              NIVEAUX.map((t) =>
                comptes[t] ? (
                  <span
                    key={t}
                    className={`fit-${t}`}
                    style={{ flexGrow: comptes[t] }}
                  />
                ) : null,
              )
            )}
          </span>
        </>
      }
    >
      <p className="fit-phrase">
        <b>{requete.nom}</b>{" "}
        {cours
          ? `: match en cours, ${cours.faits} annonces sur ${cours.total}.`
          : requete.enregistree
            ? `: ${utiles} annonces sur ${scores.length} passent le seuil de ${requete.seuils.bas} dans cette recherche.`
            : "n'a pas encore de requête. Réglez les filtres (métiers, montant, distance), puis enregistrez-les : Jev mesurera ce que chaque annonce vaut pour elle."}
        {run && !cours && (
          <span className="discret">
            {" "}
            Dernier match le {dateLongue(run.le.replace("T", " "))},{" "}
            {run.matchees} sur {run.selection}, {dollars(run.cout)}.
          </span>
        )}
        {perime && (
          <span className="fit-signal">
            {" "}
            Les filtres ont changé depuis l&apos;enregistrement.
          </span>
        )}
        {run?.erreurs.length ? (
          <span className="fit-signal"> {run.erreurs[0]}</span>
        ) : null}
      </p>

      <div className="fit-rampe" aria-hidden>
        {NIVEAUX.map((t, i) => (
          <span
            key={t}
            className={`fit-${t}`}
            style={{ width: `${Math.max(0, largeurs[i])}%` }}
            title={`${comptes[t] ?? 0} annonces`}
          />
        ))}
      </div>
      <div className="fit-seuils">
        <label title="Sous ce score, l'annonce est hors fit : une seule barre, la plus pâle">
          Fit dès
          <input
            type="number"
            min={0}
            max={100}
            value={seuils.bas}
            onChange={(e) =>
              setSeuils((s) => ({ ...s, bas: Number(e.target.value) }))
            }
          />
        </label>
        <label title="À partir de ce score, la jauge est pleine, en vert foncé">
          plein dès
          <input
            type="number"
            min={0}
            max={100}
            value={seuils.haut}
            onChange={(e) =>
              setSeuils((s) => ({ ...s, haut: Number(e.target.value) }))
            }
          />
        </label>
      </div>

      <div className="fit-montrer" role="group" aria-label="Annonces montrées">
        <span className="discret">Montrer</span>
        {[0, ...MATCHS].map((m) => (
          <button
            key={m}
            type="button"
            className={matchMin === m ? "actif" : undefined}
            aria-pressed={matchMin === m}
            disabled={!requete.matchs}
            onClick={() => onMatchMin(m)}
          >
            {m ? `dès ${m}` : "toutes"}
          </button>
        ))}
      </div>

      <div className="fit-actions">
        <button
          type="button"
          className={
            !requete.enregistree || perime || seuilsChanges
              ? undefined
              : "secondaire"
          }
          disabled={occupe || !!cours || seuils.bas >= seuils.haut}
          title={
            run
              ? `Enregistre les filtres et les seuils, puis relance le match (le dernier a coûté ${dollars(run.cout)})`
              : "Enregistre les filtres et les seuils, puis lance le match"
          }
          onClick={() => onEnregistrer(seuils)}
        >
          {occupe ? "Enregistrement…" : "Enregistrer ces filtres"}
        </button>
        {requete.enregistree && perime && (
          <button type="button" className="secondaire" onClick={onAppliquer}>
            Revenir à la requête
          </button>
        )}
        {requete.enregistree && (
          <button
            type="button"
            className="lien"
            disabled={occupe || !!cours}
            onClick={onRelancer}
          >
            Relancer le match
          </button>
        )}
      </div>
    </Volet>
  );
}

// Le message prérempli de « Discuter avec le Veilleur » : l'annonce par son identifiant, qu'il la lise, puis la question.
const aProposDe = (a: { id: string; objet: string; acheteur: string }) =>
  `À propos de l'appel d'offres ${a.id}, « ${a.objet} » (${a.acheteur}) : `;

export function Marketplace({
  annonces,
  onPrendre,
  onDiscuter,
}: {
  annonces: Annonce[];
  onPrendre: (id: string) => Promise<void>;
  onDiscuter?: (texte: string) => void; // ouvre le fil du Veilleur, le message prérempli
}) {
  const [filtres, setFiltres] = useState<Filtres>(FILTRES);
  const [saisie, setSaisie] = useState({ q: "", ville: "" });
  const [resultat, setResultat] = useState<Recherche | null>(null);
  const [liste, setListe] = useState<Annonce[]>([]);
  const [chargement, setChargement] = useState(false);
  const [erreurRecherche, setErreurRecherche] = useState<string | null>(null);
  const [enCours, setEnCours] = useState<string | null>(null);
  const [erreur, setErreur] = useState<{ id: string; message: string } | null>(
    null,
  );
  const [requete, setRequete] = useState<RequeteEntreprise | null>(null);
  const [enregistrement, setEnregistrement] = useState(false);
  const [rafraichir, setRafraichir] = useState(0);

  const [pret, setPret] = useState(false);
  const libelles = resultat?.libelles ?? SANS_LIBELLES;
  // Les libellés (dont les bornes des tranches de montant) ne changent pas d'une recherche à l'autre : la recherche
  // les lit sans dépendre d'eux.
  const libellesLus = useRef(libelles);
  libellesLus.current = libelles;

  // Les filtres de la dernière visite (dans l'onglet du navigateur), relus avant la première recherche.
  useEffect(() => {
    const f = lireFiltres();
    setFiltres(f);
    setSaisie({ q: f.q, ville: f.ville });
    setPret(true);
  }, []);

  // La requête de l'entreprise ; à la fin d'un match, elle et la recherche se rechargent.
  const chargerRequete = useCallback(async () => {
    try {
      setRequete(await api.requete());
    } catch {
      // l'API ne répond pas : la recherche le dira
    }
  }, []);
  useEffect(() => {
    void chargerRequete();
    // La connexion partagée (lib/flux) : le navigateur n'en ouvre que six par hôte, les flux de l'API les épuisent vite.
    return ecouter(
      (e) => {
        if (e.type === "sourcing" && e.action === "match") {
          void chargerRequete();
          setRafraichir((n) => n + 1);
        }
      },
      () => void chargerRequete(),
    );
  }, [chargerRequete]);
  const matchEnCours = !!requete?.en_cours;
  useEffect(() => {
    if (!matchEnCours) return;
    const t = window.setInterval(() => void chargerRequete(), 2000);
    return () => window.clearInterval(t);
  }, [matchEnCours, chargerRequete]);

  // Le texte se cherche en tapant, un peu après la dernière touche ; la ville se géocode quand on la valide.
  useEffect(() => {
    if (saisie.q === filtres.q) return;
    const t = window.setTimeout(
      () => setFiltres((f) => ({ ...f, q: saisie.q })),
      300,
    );
    return () => window.clearTimeout(t);
  }, [saisie.q, filtres.q]);

  const chercher = useCallback(
    async (offset: number) => {
      setChargement(true);
      setErreurRecherche(null);
      try {
        const r = await api.recherche({
          ...parametres(filtres, libellesLus.current),
          tri:
            filtres.tri === "distance" && !filtres.ville
              ? "date_limite"
              : filtres.tri,
          vue: filtres.vue === "carte" ? "carte" : "",
          offset,
          limit: PAGE,
        });
        setResultat(r);
        setListe((l) => (offset ? [...l, ...r.annonces] : r.annonces));
      } catch (e) {
        setErreurRecherche(
          e instanceof ErreurApi && e.code === "ville_inconnue"
            ? `Lieu introuvable : « ${filtres.ville} ».`
            : e instanceof Error
              ? e.message
              : "L'API ne répond pas.",
        );
      } finally {
        setChargement(false);
      }
    },
    [filtres],
  );

  // Une nouvelle recherche à chaque filtre, et quand les annonces changent (une prise, une passe, un match).
  useEffect(() => {
    if (!pret) return;
    try {
      window.sessionStorage.setItem(CLE_FILTRES, JSON.stringify(filtres));
    } catch {
      // le navigateur refuse : les filtres ne survivront pas à la visite
    }
    void chercher(0);
  }, [pret, filtres, chercher, annonces, rafraichir]);

  const regler = (changement: Partial<Filtres>) =>
    setFiltres((f) => ({ ...f, ...changement }));

  // Les filtres du volet ; le texte, le lieu, le tri et la vue restent.
  const effacer = () =>
    regler({
      ...Object.fromEntries(MULTIPLES.map((k) => [k, []])),
      precision: "",
      retirable: false,
      accord_cadre: "",
    });

  const prendre = async (id: string) => {
    setEnCours(id);
    setErreur(null);
    try {
      await onPrendre(id);
    } catch (e) {
      setErreur({
        id,
        message: e instanceof Error ? e.message : "L'API ne répond pas.",
      });
    } finally {
      setEnCours(null);
    }
  };

  const enregistrer = async (seuils: Seuils) => {
    setEnregistrement(true);
    try {
      const criteres = parametres(filtres, libelles);
      delete criteres.match_min; // le match se mesure sur la sélection, pas l'inverse
      setRequete(
        await api.enregistrerRequete({ criteres, seuils, auto: true }),
      );
    } catch (e) {
      setErreurRecherche(
        e instanceof Error ? e.message : "L'API ne répond pas.",
      );
    } finally {
      setEnregistrement(false);
    }
  };

  const appliquer = () => {
    if (!requete) return;
    const f = depuisCriteres(requete.criteres, libelles, filtres);
    setFiltres(f);
    setSaisie({ q: f.q, ville: f.ville });
  };

  const relancer = async () => {
    try {
      await api.lancerMatch();
      await chargerRequete();
    } catch (e) {
      setErreurRecherche(
        e instanceof Error ? e.message : "L'API ne répond pas.",
      );
    }
  };

  const f = resultat?.facettes;
  const L = resultat?.lieux;
  const seuils = requete?.seuils ?? { bas: 30, haut: 90 };
  const total = resultat?.total ?? annonces.length;
  // Les filtres ont-ils bougé depuis l'enregistrement de la requête ? (Il faut les libellés : les tranches de montant.)
  const perime =
    !!requete?.enregistree &&
    !!resultat &&
    empreinte(parametres(filtres, libelles)) !== empreinte(requete.criteres);
  const actifs =
    [filtres.precision, filtres.accord_cadre, filtres.retirable].filter(Boolean)
      .length + MULTIPLES.reduce((n, k) => n + filtres[k].length, 0);
  const villeValidee = (v: string) => {
    if (v.trim() !== filtres.ville)
      regler({
        ville: v.trim(),
        tri: v.trim() ? "distance" : "date_limite",
      });
  };

  return (
    <section className="vue marketplace">
      <header className="mk-entete">
        <h2>Marketplace</h2>
        <span
          className="mk-total"
          title={
            resultat?.centre
              ? `${total} appels d'offres autour de ${resultat.centre.label}`
              : `${total} appels d'offres`
          }
        >
          {total}
        </span>
        <input
          type="search"
          className="mk-q"
          placeholder="Objet, acheteur, référence…"
          value={saisie.q}
          onChange={(e) => setSaisie((s) => ({ ...s, q: e.target.value }))}
          aria-label="Chercher"
        />
        <select
          className="mk-zone"
          value={filtres.zone}
          onChange={(e) => regler({ zone: e.target.value })}
          aria-label="Zone"
        >
          <option value="">Toute la zone</option>
          {L &&
            Object.entries(L.regions).map(([code, r]) => (
              <optgroup key={code} label={r.nom}>
                {r.departements.length > 1 && (
                  <option value={`r:${code}`}>{r.nom} (toute la région)</option>
                )}
                {r.departements.map((d) => (
                  <option key={d} value={`d:${d}`}>
                    {d} {L.departements[d]} ({f?.departements[d] ?? 0})
                  </option>
                ))}
              </optgroup>
            ))}
        </select>
        <span className="mk-ville">
          <input
            type="search"
            placeholder="Ville ou adresse"
            value={saisie.ville}
            onChange={(e) => {
              setSaisie((s) => ({ ...s, ville: e.target.value }));
              if (!e.target.value && filtres.ville)
                regler({ ville: "", tri: "date_limite" });
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") villeValidee(saisie.ville);
            }}
            onBlur={() => villeValidee(saisie.ville)}
            aria-label="Autour de"
          />
          {filtres.ville && (
            <select
              value={filtres.rayon}
              onChange={(e) => regler({ rayon: Number(e.target.value) })}
              aria-label="Rayon"
            >
              {RAYONS.map((r) => (
                <option key={r} value={r}>
                  {r} km
                </option>
              ))}
            </select>
          )}
        </span>
        <Volet
          id="volet-filtres"
          classe={`mk-bouton${actifs ? " actif" : ""}`}
          bouton={
            <>
              Filtres
              {actifs > 0 && <span className="mk-nombre">{actifs}</span>}
            </>
          }
        >
          {/* Le chantier d'abord : ce que Jev lit de l'avis (lecture) et ce que l'avis en dit (fiche). */}
          <Puces
            legende="Corps d'état"
            options={libelles.corps}
            comptes={f?.corps}
            choisis={filtres.corps}
            onChange={(corps) => regler({ corps })}
          />
          <Puces
            legende="Nature"
            options={libelles.natures}
            comptes={f?.natures}
            choisis={filtres.nature}
            onChange={(nature) => regler({ nature })}
          />
          <Puces
            legende="Ouvrage"
            options={libelles.ouvrages}
            comptes={f?.ouvrages}
            choisis={filtres.ouvrage}
            onChange={(ouvrage) => regler({ ouvrage })}
          />
          <Puces
            legende="Montant"
            options={Object.fromEntries(
              Object.entries(libelles.tranches).map(([k, t]) => [
                k,
                trancheCourte(t),
              ]),
            )}
            comptes={f?.tranches}
            choisis={filtres.montant}
            onChange={(montant) => regler({ montant })}
          />
          <Puces
            legende="Complexité"
            options={Object.fromEntries(
              libelles.complexites.map((c, i) => [String(i), c]),
            )}
            comptes={f?.complexites}
            choisis={filtres.complexite}
            onChange={(complexite) => regler({ complexite })}
          />
          <Puces
            legende="Lots"
            options={LOTS}
            choisis={filtres.lots}
            onChange={(lots) => regler({ lots })}
          />
          <Puces
            legende="Traits (tous)"
            options={libelles.traits}
            comptes={f?.traits}
            choisis={filtres.traits}
            onChange={(traits) => regler({ traits })}
          />
          <Puces
            legende="Catégorie"
            options={CATEGORIES}
            comptes={f?.categories}
            choisis={filtres.categorie}
            onChange={(categorie) => regler({ categorie })}
          />
          <Puces
            legende="Source"
            options={ORIGINES}
            comptes={f?.origines}
            choisis={filtres.origine}
            onChange={(origine) => regler({ origine })}
          />
          <Puces
            legende="Lieu connu"
            seul
            options={PRECISIONS_LIEU}
            choisis={filtres.precision ? [filtres.precision] : []}
            onChange={([p = ""]) => regler({ precision: p })}
          />
          <Puces
            legende="Accord-cadre"
            seul
            options={ACCORDS}
            choisis={filtres.accord_cadre ? [filtres.accord_cadre] : []}
            onChange={([a = ""]) => regler({ accord_cadre: a })}
          />
          <Puces
            legende="Dossier"
            options={{ retirable: "DCE retirable" }}
            choisis={filtres.retirable ? ["retirable"] : []}
            onChange={(r) => regler({ retirable: r.length > 0 })}
          />
          <p className="mk-pied">
            <span className="discret">
              {resultat
                ? `${resultat.lues} annonce${resultat.lues > 1 ? "s" : ""} lue${resultat.lues > 1 ? "s" : ""} par Jev.`
                : ""}{" "}
              <Link href="/admin/sourcing">Sourcing</Link>
            </span>
            {actifs > 0 && (
              <button type="button" className="lien" onClick={effacer}>
                Tout effacer
              </button>
            )}
          </p>
        </Volet>
        <select
          className="mk-tri"
          value={filtres.tri}
          onChange={(e) => regler({ tri: e.target.value })}
          aria-label="Tri"
        >
          <option value="date_limite">Par échéance</option>
          <option value="publie">Plus récents</option>
          <option value="distance" disabled={!filtres.ville}>
            Par distance
          </option>
          <option value="montant">Par montant</option>
          <option value="match" disabled={!requete?.matchs}>
            Par match
          </option>
        </select>
        <span className="mk-fin">
          {requete && (
            <Fit
              requete={requete}
              scores={f?.scores ?? []}
              perime={perime}
              occupe={enregistrement}
              matchMin={filtres.match_min}
              onMatchMin={(match_min) => regler({ match_min })}
              onEnregistrer={(s) => void enregistrer(s)}
              onAppliquer={appliquer}
              onRelancer={() => void relancer()}
            />
          )}
          <span className="bascule-vue" role="group" aria-label="Vue">
            {(["liste", "carte"] as const).map((v) => (
              <button
                key={v}
                type="button"
                className={filtres.vue === v ? "actif" : undefined}
                aria-pressed={filtres.vue === v}
                onClick={() => regler({ vue: v })}
              >
                {v === "liste" ? "Liste" : "Carte"}
              </button>
            ))}
          </span>
        </span>
      </header>

      {erreurRecherche && <p className="bandeau danger">{erreurRecherche}</p>}
      {resultat && resultat.total === 0 && !chargement && (
        <p className="vide">Aucun appel d&apos;offres pour ces filtres.</p>
      )}

      {filtres.vue === "carte" ? (
        <div className="carte-vue">
          <CarteAnnonces
            points={resultat?.points ?? []}
            centre={resultat?.centre ?? null}
            siege={resultat?.siege ?? null}
            rayon={filtres.ville ? filtres.rayon : 0}
            seuils={seuils}
            raisons={requete?.raisons}
            libelleCorps={libelles.corps}
            onPrendre={(id) => void prendre(id)}
            onDiscuter={
              onDiscuter ? (p) => onDiscuter(aProposDe(p)) : undefined
            }
          />
          {resultat && (
            <p className="discret carte-legende">
              {resultat.points?.length ?? 0} annonces sur la carte
              {resultat.siege
                ? ` · en bleu : ${resultat.siege.nom || "votre entreprise"}, ${resultat.siege.adresse}`
                : ""}
              {resultat.non_situees
                ? ` · ${resultat.non_situees} sans lieu connu, absentes`
                : ""}
              {requete?.matchs
                ? " · couleur : le match de l'entreprise"
                : " · couleur : la précision du lieu"}
              {erreur && ` · ${erreur.message}`}
            </p>
          )}
        </div>
      ) : (
        <>
          <ol className={`annonces${chargement ? " charge" : ""}`}>
            {liste.map((a) => (
              <CarteAnnonce
                key={a.id}
                a={a}
                libelles={libelles}
                seuils={seuils}
                raisons={requete?.raisons}
                avecPile={!!requete?.enregistree}
                enCours={enCours}
                erreur={erreur?.id === a.id ? erreur.message : null}
                onPrendre={(id) => void prendre(id)}
                onDiscuter={
                  onDiscuter ? () => onDiscuter(aProposDe(a)) : undefined
                }
              />
            ))}
          </ol>
          {resultat && liste.length < resultat.total && (
            <p className="annonces-suite">
              <button
                type="button"
                className="secondaire"
                disabled={chargement}
                onClick={() => void chercher(liste.length)}
              >
                {chargement
                  ? "Chargement…"
                  : `Voir ${Math.min(PAGE, resultat.total - liste.length)} de plus (${liste.length} sur ${resultat.total})`}
              </button>
            </p>
          )}
        </>
      )}
    </section>
  );
}

// Une annonce de la liste : l'échéance, l'avis et ce qu'on sait du chantier, la pile du match et de quoi la prendre.
function CarteAnnonce({
  a,
  libelles,
  seuils,
  raisons,
  avecPile,
  enCours,
  erreur,
  onPrendre,
  onDiscuter,
}: {
  a: Annonce;
  libelles: Libelles;
  seuils: Seuils;
  raisons?: Record<string, string>;
  avecPile: boolean;
  enCours: string | null;
  erreur: string | null;
  onPrendre: (id: string) => void;
  onDiscuter?: () => void;
}) {
  const jours = joursAvant(a.date_limite);
  const lu = a.lecture;
  const lots = a.fiche?.lots ?? [];
  return (
    <li className={`annonce${a.pris ? " prise" : ""}`}>
      <div
        className={`projet-remise ${jours !== null && jours <= 7 ? "proche" : ""}`}
      >
        <span className="projet-jours">
          {jours === null ? "—" : echeance(jours)}
        </span>
        <span className="projet-date">remise {dateLongue(a.date_limite)}</span>
      </div>
      <div className="annonce-corps">
        <h3 className="projet-titre">{a.objet}</h3>
        <p className="projet-acheteur">
          {a.acheteur}
          {a.reference && <span className="discret"> · {a.reference}</span>}
        </p>
        <p className="annonce-lieu-ligne">
          <Lieu a={a} />
        </p>
        {a.resume && <p className="annonce-resume">{a.resume}</p>}
        {lu && lu.tags.length > 0 && (
          <p className="annonce-metiers">
            {lu.tags.map((k) => (
              <span
                key={k}
                className={`etiquette${k === lu.principal.choix ? " principale" : ""}`}
                title={
                  k === lu.principal.choix
                    ? "Le corps d'état principal"
                    : undefined
                }
              >
                {libelles.corps[k] ?? k}
              </span>
            ))}
          </p>
        )}
        <p className="annonce-faits annonce-chantier">
          {a.montant && (
            <span
              title={
                a.montant.estime
                  ? "Estimé par Jev d'après l'ampleur des travaux : l'avis ne donne pas de montant"
                  : "Le montant estimé que donne l'avis"
              }
            >
              {texteMontant(a.montant)}
            </span>
          )}
          {a.fiche?.duree_mois ? (
            <span>{duree(a.fiche.duree_mois)}</span>
          ) : null}
          {lu && (
            <span>{libelles.natures[lu.nature.choix] ?? lu.nature.choix}</span>
          )}
          {lu && (
            <span>
              {libelles.ouvrages[lu.ouvrage.choix] ?? lu.ouvrage.choix}
            </span>
          )}
          {lu && libelles.complexites[lu.complexite.niveau] && (
            <span>
              complexité{" "}
              {libelles.complexites[lu.complexite.niveau].toLowerCase()}
            </span>
          )}
          {a.fiche?.accord_cadre && <span>accord-cadre</span>}
          {a.fiche?.visite && <span>visite obligatoire</span>}
          {lu?.marques.map((k) => (
            <span key={k} className="marque">
              {libelles.traits[k] ?? k}
            </span>
          ))}
        </p>
        {lots.length > 1 && (
          <details className="annonce-lots">
            <summary>{lots.length} lots</summary>
            <ol>
              {lots.map((l, i) => (
                <li key={`${l.numero}-${i}`}>
                  <span className="discret">{l.numero}.</span> {l.intitule}
                </li>
              ))}
            </ol>
          </details>
        )}
        <p className="annonce-faits">
          <span>{a.categorie}</span>
          <span>{PROCEDURES[a.procedure] ?? a.procedure}</span>
          {a.lots && lots.length <= 1 ? <span>{a.lots} lots</span> : null}
          {a.cpv && <span>CPV {a.cpv}</span>}
          <span>{ORIGINES[a.origine ?? "manuel"]}</span>
          {a.plateforme && (
            <span>
              {a.plateforme}
              {a.editeur && EDITEURS[a.editeur]
                ? ` (${EDITEURS[a.editeur]})`
                : ""}
            </span>
          )}
          {a.rectificatifs?.length ? (
            <span>
              {a.rectificatifs.length} rectificatif
              {a.rectificatifs.length > 1 ? "s" : ""}
            </span>
          ) : null}
          <span>
            {a.dce.fichiers
              ? `DCE : ${a.dce.fichiers} fichier${a.dce.fichiers > 1 ? "s" : ""}, ${taille(a.dce.octets)}`
              : a.dce.retirable
                ? "DCE retiré à la prise"
                : "DCE à retirer sur la plateforme"}
          </span>
          {a.avis && (
            <a href={a.avis} target="_blank" rel="noreferrer">
              Avis ↗
            </a>
          )}
          {a.source && (
            <a href={a.source} target="_blank" rel="noreferrer">
              Consultation ↗
            </a>
          )}
        </p>
        {erreur && <p className="annonce-erreur">{erreur}</p>}
      </div>
      <div className="annonce-action">
        {avecPile && <Pile match={a.match} seuils={seuils} raisons={raisons} />}
        {a.pris ? (
          <Link href={`/projets/${a.id}`} className="bouton secondaire">
            Pris : ouvrir
          </Link>
        ) : (
          <button
            type="button"
            className="bouton"
            disabled={enCours !== null || !a.dce.retirable}
            title={
              a.dce.retirable
                ? undefined
                : "Cette plateforme ne laisse pas retirer le DCE sans compte : à faire à la main"
            }
            onClick={() => onPrendre(a.id)}
          >
            {enCours === a.id ? "Import du DCE…" : "Prendre"}
          </button>
        )}
        {!a.pris && !a.dce.retirable && (
          <span className="annonce-motif">DCE à retirer à la main</span>
        )}
        {onDiscuter && (
          <button
            type="button"
            className="bouton secondaire"
            onClick={onDiscuter}
          >
            Discuter avec le Veilleur
          </button>
        )}
      </div>
    </li>
  );
}

export function SqueletteMarketplace() {
  return (
    <section
      className="vue marketplace squelette"
      aria-busy="true"
      aria-label="Chargement de la Marketplace"
    >
      <header className="projets-entete">
        <Trait className="trait-h2" largeur={30} />
      </header>
      <ol className="annonces">
        {[70, 56, 64].map((l, k) => (
          <li key={k} className="annonce">
            <div className="projet-remise">
              <Trait className="trait-gras" largeur={70} i={k * 3} />
            </div>
            <div className="annonce-corps">
              <Trait className="trait-gras" largeur={l} i={k * 3 + 1} />
              <Trait className="trait-petit" largeur={l - 20} i={k * 3 + 2} />
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}
